import React, { useState } from "react";
import * as m from "../../lib/model";
import { debtApi } from "../../lib/api";
import { Card } from "../shared";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Pencil, Trash2, Plus, X, Mail } from "lucide-react";
import { DEBT_STAGES } from "./DebtModal";
import { Avatar } from "../team/TeamChips";
import { Attachments } from "./Attachments";

export const DebtRow = ({ lead, ebitda, today, checked, expanded, onCheck, onExpand, onChange, onEdit, onRemove }) => {
  const overdue = lead.nextFollowUp && lead.nextFollowUp < today;
  const hot = ["Term Sheet", "Negotiating", "Committed"].includes(lead.stage);
  const setStage = async (stage) => { try { onChange(await debtApi.patch(lead.id, { stage })); } catch { toast.error("Could not update"); } };
  return (
    <tr className={`cm-lead-row ${hot ? "hot" : ""} ${lead.stage === "Passed" ? "passed" : ""}`} data-testid={`debt-row-${lead.id}`}>
      <td><input type="checkbox" checked={checked} onChange={onCheck} data-testid={`debt-check-${lead.id}`} /></td>
      <td>
        <button className="btn" style={{ padding: "1px 4px", marginRight: 5 }} onClick={() => onExpand(lead.id)} data-testid={`debt-expand-${lead.id}`}>{expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</button>
        {hot && <span className="lead-name-dot" />}{lead.lender}
        {lead.owner ? <span className="lead-owner"><Avatar name={lead.owner} email={lead.owner} size={18} title={`Owner: ${lead.owner}`} /></span> : null}
      </td>
      <td>{lead.contact || "—"}<div className="tc-sub">{[lead.bankerTitle, lead.email].filter(Boolean).join(" · ")}</div></td>
      <td><span className="tag">{lead.type}</span></td>
      <td><select className="inp cm-cell" value={lead.stage} onChange={(e) => setStage(e.target.value)} data-testid={`debt-stage-${lead.id}`}>{DEBT_STAGES.map((s) => <option key={s}>{s}</option>)}</select></td>
      <td className="num">{lead.amount > 0 ? m.fmt$(lead.amount) : "—"}</td>
      <td className="num">{lead.amount > 0 && ebitda > 0 ? m.fmtX(lead.amount / ebitda) : "—"}</td>
      <td className="num">{lead.rate > 0 ? `${(+lead.rate).toFixed(2)}%` : "—"}</td>
      <td className="num">{lead.termYears ? `${lead.termYears}/${lead.amortYears || lead.termYears}` : "—"}</td>
      <td className="num">{lead.closingDays ? `${lead.closingDays}d` : "—"}</td>
      <td>{lead.recourse}</td>
      <td className={overdue ? "neg" : ""}>{lead.nextFollowUp || "—"}</td>
      <td><button className="btn" style={{ padding: "2px 6px" }} onClick={() => onEdit(lead)} data-testid={`debt-edit-${lead.id}`}><Pencil size={12} /></button></td>
      <td><button className="btn" style={{ padding: "2px 6px" }} onClick={() => onRemove(lead)} data-testid={`debt-remove-${lead.id}`}><Trash2 size={12} /></button></td>
    </tr>
  );
};

export const DebtDetail = ({ lead, today, dealName, M, onChange, onClose }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [mail, setMail] = useState(null);
  const logNote = async () => {
    if (!note.trim()) return;
    setBusy("note");
    try { onChange(await debtApi.activity(lead.id, { kind: "Note", note })); setNote(""); } finally { setBusy(""); }
  };
  const openMail = () => setMail({
    subject: `Financing request — ${dealName}`,
    body: `Hi ${lead.contact || lead.lender},\n\nFollowing up on the senior financing for ${dealName}.\n\nAdj. EBITDA: ${m.fmt$(M.sde)}\nPurchase price: ${m.fmt$(M.askPrice)}\nRequested facility: ${m.fmt$(lead.amount || m.loanAmt(M))} (${M.sde ? m.fmtX((lead.amount || m.loanAmt(M)) / M.sde) : "—"} EBITDA)\nYear-1 DSCR at our base case: ${m.fmtX(m.dscrYr1(M))}\n\nCould you share indicative terms — amount, rate, amortization, recourse and timing to close? Happy to send the full package under NDA.\n\nXYZ Ventures`,
  });
  const send = async () => {
    setBusy("send");
    try {
      const r = await debtApi.sendEmail(lead.id, mail);
      toast[r.delivered ? "success" : "info"](r.delivered ? `Sent to ${lead.email}` : "Logged (email key missing)");
      setMail(null); onChange(await debtApi.patch(lead.id, { lastContact: today }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(""); }
  };
  return (
    <Card title={`${lead.lender}${lead.contact ? ` · ${lead.contact}` : ""}`} style={{ marginTop: 12 }} testid={`debt-detail-${lead.id}`}
      right={<button className="btn" onClick={onClose} data-testid="debt-detail-close"><X size={13} />Close</button>}>
      <div className="cm-detail-grid">
        <div>
          <div className="section-label">Proposal</div>
          {[["Banker", [lead.contact, lead.bankerTitle].filter(Boolean).join(", ") || "—"], ["Facility", lead.facility || "—"], ["Amount", lead.amount > 0 ? m.fmt$(lead.amount) : "—"], ["Rate", lead.rate ? `${lead.rate}% ${lead.rateType || ""}${lead.indexRate ? ` (${lead.indexRate}${lead.spread ? ` +${lead.spread}%` : ""})` : ""}` : "—"], ["Term / amort", `${lead.termYears || "—"} / ${lead.amortYears || "—"} yrs`], ["Term sheet received", lead.termReceived || "—"], ["Expires", lead.termExpires || "—"],
            ["Time to close", lead.closingDays ? `${lead.closingDays} days` : "—"], ["Recourse", lead.recourse], ["Fees", lead.feesPct ? `${lead.feesPct}%` : "—"], ["Prepayment", lead.prepay || "—"],
            ["Collateral", lead.collateral || "—"], ["Covenants", lead.covenants || "—"], ["Phone", lead.phone || "—"], ["Source", lead.source || "—"], ["Owner", lead.owner || "—"]].map(([k, v]) => (
              <div key={k} className="cm-kv"><span>{k}</span><b>{v}</b></div>
            ))}
          {lead.notes && <div className="cm-notes">{lead.notes}</div>}
          <div className="section-label" style={{ marginTop: 10 }}>Term sheet files</div>
          <Attachments lead={lead} onChange={onChange} />
        </div>
        <div>
          <div className="section-label">Activity</div>
          <div className="cm-log">
            {(lead.activities || []).length === 0 && <div className="cm-empty" style={{ padding: 10 }}>Nothing logged yet.</div>}
            {[...(lead.activities || [])].reverse().map((a) => (
              <div key={a.id} className="cm-log-row"><span className="cl-kind">{a.kind}</span><span className="cl-note">{a.note}</span><span className="cl-at">{(a.at || "").slice(0, 10)}</span></div>
            ))}
          </div>
          <div className="cm-note-add">
            <input className="inp" placeholder="Log a call / meeting note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && logNote()} data-testid={`debt-note-input-${lead.id}`} />
            <button className="btn" onClick={logNote} disabled={busy === "note" || !note.trim()} data-testid={`debt-note-add-${lead.id}`}><Plus size={13} />Log</button>
          </div>
          <div className="cm-email">
            <button className="btn" onClick={openMail} disabled={!lead.email} data-testid={`debt-mail-${lead.id}`}><Mail size={13} />Request terms by email</button>
            {mail && (
              <div className="cm-draft">
                <input className="inp inp-text" value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} data-testid={`debt-mail-subject-${lead.id}`} />
                <textarea className="inp" rows={8} value={mail.body} onChange={(e) => setMail({ ...mail, body: e.target.value })} data-testid={`debt-mail-body-${lead.id}`} />
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn btn-primary" onClick={send} disabled={busy === "send"} data-testid={`debt-mail-send-${lead.id}`}>{busy === "send" ? "Sending…" : `Send to ${lead.email}`}</button>
                  <button className="btn" onClick={() => setMail(null)}>Discard</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
